import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Tabs,
  Tab
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import trackingService from '../../services/trackingService';

const TrackingLogin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0 = login, 1 = setup
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Setup form
  const [setupEmail, setSetupEmail] = useState('');
  const [setupName, setSetupName] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirmPassword, setSetupConfirmPassword] = useState('');

  useEffect(() => {
    // Redirect if already authenticated
    if (trackingService.isAuthenticated()) {
      navigate('/tracking/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await trackingService.login(loginEmail, loginPassword);
      if (result.success) {
        navigate('/tracking/dashboard');
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (setupPassword !== setupConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (setupPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await trackingService.setupFirstAdmin(setupEmail, setupName, setupPassword);
      if (result.success) {
        setSuccess('Admin account created! You can now login.');
        setTab(0);
        setLoginEmail(setupEmail);
        // Clear setup form
        setSetupEmail('');
        setSetupName('');
        setSetupPassword('');
        setSetupConfirmPassword('');
      } else {
        setError(result.message || 'Setup failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Admin may already exist.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ bgcolor: '#0f0f23', color: 'white' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: '#00d4ff' }}>
              Ailyce Team Tracking
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 3, color: 'grey.400' }}>
              Monitor team Claude Code usage
            </Typography>

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              centered
              sx={{
                mb: 3,
                '& .MuiTab-root': { color: 'grey.400' },
                '& .Mui-selected': { color: '#00d4ff' },
                '& .MuiTabs-indicator': { bgcolor: '#00d4ff' }
              }}
            >
              <Tab label="Login" />
              <Tab label="First Time Setup" />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {tab === 0 ? (
              // Login Form
              <form onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' }
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    bgcolor: '#00d4ff',
                    color: '#0f0f23',
                    '&:hover': { bgcolor: '#00b8e6' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Login'}
                </Button>
              </form>
            ) : (
              // Setup Form
              <form onSubmit={handleSetup}>
                <Typography variant="body2" sx={{ mb: 2, color: 'grey.400' }}>
                  Create the first admin account. This only works if no admin exists.
                </Typography>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={setupEmail}
                  onChange={(e) => setSetupEmail(e.target.value)}
                  required
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Full Name"
                  value={setupName}
                  onChange={(e) => setSetupName(e.target.value)}
                  required
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  required
                  margin="normal"
                  helperText="Minimum 8 characters"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' },
                    '& .MuiFormHelperText-root': { color: 'grey.500' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={setupConfirmPassword}
                  onChange={(e) => setSetupConfirmPassword(e.target.value)}
                  required
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
                    },
                    '& .MuiInputLabel-root': { color: 'grey.400' }
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    bgcolor: '#00d4ff',
                    color: '#0f0f23',
                    '&:hover': { bgcolor: '#00b8e6' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Admin Account'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TrackingLogin;
